import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Eventcalendar, MbscCalendarEvent,} from "@mobiscroll/react";
import Box from "@mui/material/Box";
import {useCurrentApp, useCurrentAppId} from "crqlar-react-common/dist/utils/hooks/app/useOrganizationApps";
import {Reservation, ReservationStatus,} from "crqlar-react-common/dist/models/reservation/reservation.model";
import {addMinutes} from "crqlar-react-common/dist/utils/date.util";
import {ReservationActions, useReservationsState} from "../../../store/reservations.reducer";
import ReservationDialog from "../reservationDialog/ReservationDialog";
import {useDispatch} from "react-redux";
import {
    statusFilterOptionsBasedOnView,
    useShouldDisplayChildrenResources,
} from "../../../utils/reservation.util";
import {SectorType} from "crqlar-react-common/dist/models/sector/sector.model";
import ConfirmReservationUpdateDialog from "./ConfirmReservationUpdateDialog";
import {hasRequiredResources} from "crqlar-react-common/dist/utils/hooks/reservations/reservations.utils";
import {
    AVAILABILITY_QUERY_KEY,
    calculateEarliestReservationByDateRange,
    getFullDayRange,
    getTimelineSpanDates,
    inUnassignedRow,
    RESERVATIONS_QUERY_KEY,
    SECTORS_SHOW_CALENDAR_AVAILABILITY,
    SHOW_PACKAGE_ICON_SECTOR_TYPES,
} from "crqlar-react-common/dist/utils/hooks/reservations/calendar/calendar.util";
import CalendarHeader from "./CalendarHeader";
import CalendarComponent from "./CalendarComponent";
import BlockWholeDayPopup from "./BlockWholeDayPopup";
import {
    AvailabilityCalculationResult
} from "crqlar-react-common/dist/models/availability/availabilityCalculationResult.model";
import BlockPeriodDialog from "./BlockPeriodDialog";
import "@mobiscroll/print/dist/css/mobiscroll.min.css";
import './ReservationTimelineTab.css'
import {MarkedService} from "crqlar-react-common/dist/models/service/service.model";
import ServiceDialog from "../services/ServiceDialog";
import {useWeeklyPlanner} from "../services/useWeeklyPlanner";
import {readSectorDate} from "../sectorDate";
import {useQueryClient} from "react-query";
import {useCalendarPrint} from "./util/useCalendarPrint";
import PackageWizardComponent from "./PackageWizardComponent";
import {EventGenerationConfig, generateReservationEvents} from "./util/calendarEventGenerator";
import {useCalendarHandlers} from "./util/useCalendarHandlers";
import CalendarResource from "./CalendarResource";
import {useMbscResources} from "./util/useMbscResources";
import {useCalendarData} from "./util/useCalendarData";
import {
    RightSidebarCreateButton,
    RightSidebarDivider,
    RightSidebarFullscreenExitButton,
    RightSidebarRail,
    RightSidebarRailHeader,
    RightSidebarRailLayout
} from "../../layout/rightSidebar/RightSidebarRail";
import {useTranslation} from "react-i18next";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import {ReservationStatusPicker} from "../rail/ReservationStatusPicker";
import {CalendarViewPicker} from "../rail/CalendarViewPicker";
import {ReservationPackageCreateButton} from "../rail/ReservationPackageCreateButton";
import {ReservationResourcePicker} from "../rail/ReservationResourcePicker";
import {useReservationResourceSelection} from "../rail/useReservationResourceSelection";
import {ReservationServicePicker} from "../rail/ReservationServicePicker";
import {useReservationServiceSelection} from "../rail/useReservationServiceSelection";
import {ReservationFullscreenButton} from "../rail/ReservationFullscreenButton";
import {useReservationFullscreen} from "../rail/useReservationFullscreen";
import {ReservationCalendarStatisticsButton} from "../rail/ReservationCalendarStatisticsButton";
import {ReservationCalendarLegendButton} from "../rail/ReservationCalendarLegendButton";
import {useCalendarViewPickerState} from "./util/useCalendarViewPickerState";
import {useCalendarAvailabilityEvents} from "./util/useCalendarAvailabilityEvents";
import {CalendarRailSearch} from "./CalendarRailSearch";

const CALENDAR_STATUSES = statusFilterOptionsBasedOnView("calendar");
// Calendar core component
// Contains state and logic for reservation calendar timeline tab
const TableReservationTimelineTab = () => {

    // =========== Base data hooks ==========
    const {t} = useTranslation();
    const appId = useCurrentAppId()
    const readOnly = useReadOnly();
    const appData = useCurrentApp()
    const calendarRef = useRef<Eventcalendar | null>(null);
    const {isFullscreen, setIsFullscreen} = useReservationFullscreen();

    const queryClient = useQueryClient()

    // ============= State section ============
    const {
        fromDate,
        toDate,
        reservationDaySlice,
        selectedStatuses,
        viewMode,
        timelineSpan,
        blockWholeDay,
        resourceCategoryFilter,
        mbscResourceColumnType,
        tmpReservationGroupWizardActive,
        tmpReservationGroupWizardReservation,
        tmpReservationGroupReservations,
        selectedAgenda,
        newReservation
    } = useReservationsState(appId);

    // Local UI state

    // reservation dialog
    const [markedReservation, setMarkedReservation] = useState<Reservation>()

    // service dialog
    const [markedService, setMarkedService] = useState<MarkedService | undefined>(undefined)

    // block period dialog
    const [markedBlockPeriod, setMarkedBlockPeriod] = useState<AvailabilityCalculationResult | undefined>(undefined)

    useEffect(() => {
        if (newReservation) {
            setMarkedReservation(
                {
                    // Fill all other necessary fields for the reservation
                    ...newReservation,
                    serviceId: defaultService?.id,
                    expectedEndDate: addMinutes(new Date(), defaultService?.durationMinutes || 60),
                    sectorId: sector?.id,
                    status: ReservationStatus.CONFIRMED
                }
            )
            // Clear the new reservation from the state, so it doesn't interfere with the list anymore (like staying marked or appearing in the top of the list)
            dispatch({
                type: ReservationActions.SET_RESERVATIONS,
                appId: appId,
                payload: {
                    newReservation: undefined
                }
            })
        }
    }, [newReservation])

    const [tmpUpdatedReservation, setTmpUpdatedReservation] = useState<Reservation>()
    const [tmpOriginalReservation, setTmpOriginalReservation] = useState<Reservation>()

    const [showAlert, setShowAlert] = useState(false)

    // Selected event from search result list
    const [selectedEventFromSearch, setSelectedEventFromSearch] = useState<MbscCalendarEvent | null>();

    const sectorId = appData?.configuration?.sectorId ?? "";
    const resourceSelection = useReservationResourceSelection({
        sectorId,
        viewKey: "calendar",
    });
    const serviceSelection = useReservationServiceSelection({
        sectorId,
        viewKey: "calendar",
    });

    // Consolidated data fetching
    const {
        sector,
        defaultService,
        resourcesFilteredByCategory,
        childrenResourcesFilteredByCategory,
        allResources,
        allChildResources,
        reservations,
        serviceReservations,
        profiles,
        availabilitiesCalculationResults,
        ageCategories,
        resourceCategories,
        sectorServices,
        services,
        restaurantSectorId,
        refetch,
        updateReservationInCache
    } = useCalendarData({
        sectorId: appData?.configuration?.sectorId,
        fromDate,
        toDate,
        selectedStatuses: selectedStatuses?.length ? selectedStatuses : [],
        selectedServices: serviceSelection.selectedServices,
        mbscResourceColumnType,
        resourceCategoryFilter,
        timelineSpan,
        selectedAgenda,
    })

    // Initially setting the fromDate and toDate to its corresponding range based on selected timelineSpan
    // The sector-wide day, shared with the floor plan and the day lists.
    const rangeFromLocalStorage = readSectorDate(sector?.id)?.date

    const startDateFinal = useMemo(() => {
        const [startDate, endDate] = getFullDayRange(rangeFromLocalStorage ?? fromDate)
        return calculateEarliestReservationByDateRange(startDate, endDate, reservations, true)
    }, [rangeFromLocalStorage, fromDate, reservations])

    const dispatch = useDispatch()

    const focusedDateTimestamp = (rangeFromLocalStorage ?? fromDate).getTime()
    const focusedDate = useMemo(() => new Date(focusedDateTimestamp), [focusedDateTimestamp])
    const calendarViewPicker = useCalendarViewPickerState({
        appId,
        sector,
        focusedDate,
    })

    const handleCreateReservation = useCallback(() => {
        if (!sector?.id) return

        const now = new Date()
        const start = new Date(rangeFromLocalStorage ?? fromDate ?? now)
        start.setHours(now.getHours(), now.getMinutes(), 0, 0)

        setMarkedReservation({
            expectedStartDate: start,
            expectedEndDate: addMinutes(start, defaultService?.durationMinutes ?? 60),
            serviceId: defaultService?.id,
            sectorId: sector.id,
            status: ReservationStatus.CONFIRMED,
        })
    }, [defaultService?.durationMinutes, defaultService?.id, fromDate, rangeFromLocalStorage, sector?.id])

    const handleStatusesChange = useCallback((statuses: typeof selectedStatuses) => {

        if (!sector?.id) return

        localStorage.setItem(`selectedStatuses-${sector.id}-calendar`, statuses.join(","));

        dispatch({
            type: ReservationActions.SET_RESERVATIONS,
            appId,
            payload: {selectedStatuses: statuses},
        });
    }, [appId, dispatch, sector?.id]);

    // Print actions
    useCalendarPrint(calendarRef)

    const usesChildrenResources = useShouldDisplayChildrenResources(sector)

    const handleRefetchData = useCallback(async () => {
        await queryClient.invalidateQueries([RESERVATIONS_QUERY_KEY])
        await queryClient.invalidateQueries([AVAILABILITY_QUERY_KEY])
    }, [queryClient])

    const handleAvailabilitiesChanged = useCallback(async () => {
        await queryClient.invalidateQueries([AVAILABILITY_QUERY_KEY])
    }, [queryClient])

    const calendarReservations = sector?.sectorType === SectorType.WEEKLY_PLANNER
        ? [...serviceReservations] // during weekly planner, we show service reservations
        : [...reservations] // during other sectors, we show all reservations

    useEffect(() => {
        const [startDate, endDate] = getTimelineSpanDates(timelineSpan, rangeFromLocalStorage ?? fromDate)

        // init the timeline span date range to the range from local storage
        if (sector?.sectorType === SectorType.WEEKLY_PLANNER) {
            dispatch({
                type: ReservationActions.SET_RESERVATIONS,
                appId: appId,
                payload: {
                    fromDate: startDate?.toISOString()
                }
            })
        }

        dispatch({
            type: ReservationActions.SET_RESERVATIONS,
            appId: appId,
            payload: {
                fromDate: startDate?.toISOString(),
                toDate: endDate.toISOString()
            }
        })
    }, [])

    const {
        availabilityColors,
        availabilityDisplayColors,
        blockPeriodEvents,
        handleBlockPeriodUpdated,
    } = useCalendarAvailabilityEvents({
        availabilityResults: availabilitiesCalculationResults,
        refetchData: handleRefetchData,
        childResources: usesChildrenResources ? allChildResources : undefined,
    })

    // Main calendar view

    const {generateServiceEvents} = useWeeklyPlanner()

    const eventGenerationConfig: EventGenerationConfig = useMemo(() => ({
        sector,
        profiles,
        services,
        resourcesFilteredByCategory,
        allResources,
        allChildResources,
        selectedResources: resourceSelection.selectedResources,
        sectorServices,
        availabilitiesCalculationResults,
        mbscResourceColumnType,
        usesChildrenResources,
        restaurantSectorId,
    }), [
        sector,
        profiles,
        services,
        resourcesFilteredByCategory,
        allResources,
        allChildResources,
        resourceSelection.selectedResources,
        sectorServices,
        availabilitiesCalculationResults,
        mbscResourceColumnType,
        usesChildrenResources,
        restaurantSectorId,
    ])

    // Only package members that already have a time slot can be drawn. Unscheduled ones used to be
    // handed to Mobiscroll with no start/end and no id.
    const activeAndTmpReservationsCombined = useMemo(
        () => [
            ...reservations,
            ...tmpReservationGroupReservations.filter(r => r.expectedStartDate && r.expectedEndDate)
        ],
        [reservations, tmpReservationGroupReservations]
    )

    const events = useMemo<MbscCalendarEvent[]>(
        () => generateReservationEvents(activeAndTmpReservationsCombined, eventGenerationConfig),
        [activeAndTmpReservationsCombined, eventGenerationConfig]
    )

    const agendaEvents = useMemo<MbscCalendarEvent[]>(() => {
        return generateServiceEvents(services, serviceReservations)
    }, [generateServiceEvents, serviceReservations, services])

    // This way we keep the unassigned reservations specific to sector
    const mbscResources = useMbscResources({
        sector: sector,
        availableResources: resourcesFilteredByCategory,
        selectedResources: resourceSelection.selectedResources,
        childrenResources: childrenResourcesFilteredByCategory,
        services: services,
        mbscResourceColumnType: mbscResourceColumnType,
        usesChildrenResources: usesChildrenResources,
    })

    // Handlers section

    const {
        handleDeselect,
        createNewReservation,
        updateReservationViaCalendarAction,
        handleClickSearchResult,
        handleEventQuickUpdate
    } = useCalendarHandlers({
        appId,
        sector,
        defaultServiceId: defaultService?.id,
        services,
        resourcesFilteredByCategory,
        childrenResourcesFilteredByCategory,
        mbscResourceColumnType,
        calendarRef,
        tmpReservationGroupWizardActive,
        tmpReservationGroupWizardReservation,
        tmpReservationGroupReservations,
        setMarkedReservation,
        selectedEventFromSearch,
        setSelectedEventFromSearch,
        setTmpUpdatedReservation,
        setTmpOriginalReservation,
        refetchData: handleRefetchData,
    })

    const calendarEvents = useMemo(
        () => {
            if (sector?.sectorType === SectorType.WEEKLY_PLANNER) {
                return [...agendaEvents, ...blockPeriodEvents]
            }
            // In timeGrid (schedule, resources-as-columns) Mobiscroll splits the column width
            // between any time-overlapping events. The transparent block-period events overlap
            // reservations and were squeezing them to 50%/33% width (Mobiscroll's stacking has no
            // per-event opt-out). Exclude block-period events from the schedule layout so
            // reservations fill the full column. The block still renders via availabilityColors
            // (colors prop) and clicking it still opens the dialog via onCellClick -> handleCellClick
            // (which reads blockPeriodEvents — that includes the restaurant block mirrored onto each
            // table, so a click on a table's red area opens the restaurant's block). Timeline keeps
            // them (rows, not columns), so block drag/resize there is unaffected — on the restaurant
            // row and on every table row alike.
            if (viewMode === 'timeGrid') {
                return [...events]
            }
            return [...events, ...blockPeriodEvents]
        },
        [sector?.sectorType, agendaEvents, blockPeriodEvents, events, viewMode]
    )

    const renderMyResource = useCallback((mbscResource: any) => {
        return (
            <CalendarResource
                mbscResource={mbscResource}
                resourcesFilteredByCategory={resourcesFilteredByCategory}
                childrenResourcesFilteredByCategory={childrenResourcesFilteredByCategory}
                sector={sector}
                viewMode={viewMode}
                restaurantSectorId={restaurantSectorId}
                onAvailabilitiesChanged={handleAvailabilitiesChanged}
            />
        )
    }, [resourcesFilteredByCategory, childrenResourcesFilteredByCategory, sector, viewMode, restaurantSectorId, handleAvailabilitiesChanged])

    const renderBlockedDayPopup = () => {
        return (
            <BlockWholeDayPopup
                blockWholeDay={blockWholeDay}
                showAlert={showAlert}
                setShowAlert={setShowAlert}
            />
        )
    }

    const updateDialog = () => {
        return (
            <ConfirmReservationUpdateDialog
                open={!!tmpUpdatedReservation}
                onClose={() => {
                    setTmpUpdatedReservation(undefined)
                    refetch()
                }}
                onConfirm={async (notify) => {
                    await handleEventQuickUpdate(tmpUpdatedReservation, notify)
                    setTmpUpdatedReservation(undefined)

                    /*
                        When user starts assigning resources, but there aren't enough being assigned ->
                        Then this will open the reservation dialog for another time - so he can assign more resources
                    */

                    const service = services.find(s => s.id === tmpUpdatedReservation?.serviceId)
                    // We need to combine resources and childrenResources in case of restaurant sector
                    const reservationResources = [...resourcesFilteredByCategory, ...childrenResourcesFilteredByCategory].filter(r => tmpUpdatedReservation?.resourceIds?.includes(r.id!))

                    if (!hasRequiredResources(service, reservationResources, restaurantSectorId)
                        && !inUnassignedRow(tmpUpdatedReservation)) {
                        setMarkedReservation(tmpUpdatedReservation)
                    }

                    refetch()
                }}
                reservation={tmpUpdatedReservation}
                originalReservation={tmpOriginalReservation}
            />
        )
    }

    return (<RightSidebarRailLayout sx={theme => ({
            height: "100%",
            overflow: "hidden",
            ...(isFullscreen && {
                position: "fixed",
                inset: 0,
                width: "100dvw",
                height: "100dvh",
                "@supports not (height: 100dvh)": {height: "100vh"},
                bgcolor: "background.default",
                overscrollBehavior: "contain",
                zIndex: theme.zIndex.modal,
            }),
            })}
        >
            <RightSidebarRailHeader compact={isFullscreen}>
                <RightSidebarFullscreenExitButton
                    active={isFullscreen}
                    onExit={() => setIsFullscreen(false)}
                />

                <CalendarHeader
                    navigatedDate={fromDate}
                    reservations={reservations}
                    services={services}
                    reservationDaySlice={reservationDaySlice}
                    timelineSpan={timelineSpan}
                    mbscResources={mbscResources}
                    mbscEvents={calendarEvents}
                />
            </RightSidebarRailHeader>

            <Box sx={{
                position: "relative",
                flex: 1,
                minWidth: 0,
                minHeight: 0,
            }}>
                    <CalendarComponent
                        timelineSpan={timelineSpan}
                        viewMode={viewMode}
                        events={calendarEvents}
                        selectedEventFromSearch={selectedEventFromSearch}
                        // for better weekly planner UX, avoid grouping by resource
                        mbscResources={viewMode === "timeGrid" && timelineSpan === "week" ? undefined : mbscResources}
                        setMarkedReservation={setMarkedReservation}
                        setMarkedService={(service, availability, reservations, startTime, endTime, hasException: boolean) => {

                            if (service && availability) {
                                setMarkedService({
                                    service,
                                    availability,
                                    reservations: reservations ?? [],
                                    startDate: startTime,
                                    endDate: endTime,
                                    isException: hasException
                                })
                            }
                        }}
                        handleDeselect={handleDeselect}
                        renderMyResource={renderMyResource}
                        mbscResourceColumnType={mbscResourceColumnType}
                        setShowAlert={setShowAlert}
                        updateReservationViaCalendarAction={updateReservationViaCalendarAction}
                        setTmpUpdatedReservation={setTmpUpdatedReservation}
                        setTmpOriginalReservation={setTmpOriginalReservation}
                        createNewReservation={createNewReservation}
                        toDate={toDate}
                        fromDate={fromDate}
                        startDateFinal={startDateFinal}
                        calendarRef={calendarRef}
                        sector={sector}
                        reservations={calendarReservations}
                        services={services}
                        serviceReservations={serviceReservations}
                        profiles={profiles}
                        availabilityColors={availabilityColors}
                        availabilityDisplayColors={availabilityDisplayColors}
                        ageCategories={ageCategories}
                        disableDragEvents={sector?.sectorType === SectorType.WEEKLY_PLANNER}
                        updateReservationInCache={updateReservationInCache}
                        blockPeriodEvents={blockPeriodEvents}
                        onBlockPeriodClick={(calcResult) => setMarkedBlockPeriod(calcResult)}
                        onBlockPeriodUpdated={handleBlockPeriodUpdated}
                    />

                    <ServiceDialog
                        open={!!markedService}
                        onClose={() => {
                            setMarkedService(undefined)
                        }}
                        markedService={markedService}
                        onSave={async () => {
                            refetch()
                        }}
                        sector={sector}
                        resourceCategories={resourceCategories}
                        profiles={profiles}
                    />

                    <ReservationDialog
                        open={!!markedReservation}
                        onClose={() => {
                            // Close immediately, then refetch in the background. Awaiting the
                            // refetch before clearing state kept the dialog fully visible for the
                            // entire network round-trip, which made closing feel slow.
                            setMarkedReservation(undefined)
                            void handleRefetchData()
                        }}
                        reservation={markedReservation}
                        availabilityResults={availabilitiesCalculationResults}
                        onSave={async (tmpRes) => {
                            await handleRefetchData()
                            setMarkedReservation(undefined)
                        }}
                        onSaveBlock={async () => {
                            await handleRefetchData()
                            setMarkedReservation(undefined)
                        }}
                        // isWalkIn={isWalkIn}
                        onChangeReservation={setMarkedReservation}
                    />

                    <BlockPeriodDialog
                        open={!!markedBlockPeriod}
                        onClose={() => setMarkedBlockPeriod(undefined)}
                        blockPeriod={markedBlockPeriod}
                        onDeleted={async () => {
                            await handleRefetchData()
                            setMarkedBlockPeriod(undefined)
                        }}
                    />

                    {renderBlockedDayPopup()}

                    {updateDialog()}

                    {/* Package booking wizard */}
                    {
                        tmpReservationGroupWizardActive &&
                        <PackageWizardComponent/>
                    }

            </Box>


            <RightSidebarRail>

                {sector?.sectorType !== SectorType.WEEKLY_PLANNER && (
                    <CalendarRailSearch
                        sector={sector}
                        fromDate={fromDate}
                        toDate={toDate}
                        selectedStatuses={selectedStatuses}
                        selectedResourceIds={resourceSelection.selectedIds}
                        eventGenerationConfig={eventGenerationConfig}
                        reservationEvents={events}
                        profiles={profiles}
                        ageCategories={ageCategories}
                        mbscResourceColumnType={mbscResourceColumnType}
                        onSelectResult={event => handleClickSearchResult({event})}
                        onEditReservation={setMarkedReservation}
                    />
                )}

                <ReservationStatusPicker
                    availableStatuses={CALENDAR_STATUSES}
                    selectedStatuses={selectedStatuses}
                    onSelectedStatusesChange={handleStatusesChange}
                />

                <CalendarViewPicker
                    value={calendarViewPicker.value}
                    capabilities={calendarViewPicker.capabilities}
                    onChange={calendarViewPicker.onChange}
                />

                <ReservationFullscreenButton
                    isFullscreen={isFullscreen}
                    onChange={setIsFullscreen}
                />

                <RightSidebarDivider/>
                <RightSidebarCreateButton
                    label={t("Create reservation")}
                    disabled={readOnly || !sector?.id}
                    onClick={handleCreateReservation}
                />
                <RightSidebarDivider/>

                {mbscResourceColumnType === "services" ? (
                    <ReservationServicePicker selection={serviceSelection}/>
                ) : (
                    <ReservationResourcePicker
                        selection={resourceSelection}
                        focusedDate={focusedDate}
                    />
                )}

                {SHOW_PACKAGE_ICON_SECTOR_TYPES.includes(sector?.sectorType as SectorType) && (
                    <ReservationPackageCreateButton/>
                )}

                {sector?.sectorType === SectorType.RESTAURANT && (
                    <ReservationCalendarStatisticsButton
                        scope={{
                            sectorId,
                            resources: resourceSelection.selectedResources,
                            fromDate,
                            toDate,
                            statuses: selectedStatuses,
                            daySlice: reservationDaySlice,
                            timelineSpan,
                        }}
                    />
                )}

                {SECTORS_SHOW_CALENDAR_AVAILABILITY.includes(sector?.sectorType as SectorType) && (
                    <ReservationCalendarLegendButton/>
                )}
            </RightSidebarRail>
    </RightSidebarRailLayout>)

}

export default TableReservationTimelineTab
